import React from "react";

import { css } from "@emotion/core";
import { Fragment, useContext } from "react";

import { CurrencyContext, Price } from "@zapier/currency";
import { DateTime } from "@zapier/date";
import {
  Link,
  Colors as ThemeColors,
  useTheme,
  Typography,
} from "@zapier/design-system";

import { MeteredTiersTable } from "@zapier/checkout/esm/components";

import { Colors } from "@zapier/checkout/esm/constants";
import { Interval, PlanTier } from "@zapier/checkout/esm/types";

type AmountDueOnNextIntervalProps = {
  extraTerm?: string | null;
  isPlanMetered: boolean;
  meteredTiers: PlanTier[];
  nextPaymentDate: string;
  planBillingAmount: number;
  planInterval: Interval;
  planNumFreeMeteredTasks: number;
};

const nextIntervalCss = css`
  background-color: ${Colors.storm};
  color: ${Colors.steel};
  padding: 10px;
`;

const rebrandNextIntervalCss = css`
  background-color: ${ThemeColors.neutral200};
  color: ${ThemeColors.neutral800};
  padding: 1.25em;
  ${Typography.smallPrint1};
`;

const lastParagraphCss = css`
  margin: 0;
`;

const tableStyle = css`
  margin-bottom: 15px;
`;

export const AmountDueOnNextInterval = ({
  extraTerm = null,
  isPlanMetered,
  meteredTiers,
  nextPaymentDate,
  planBillingAmount,
  planInterval,
  planNumFreeMeteredTasks,
}: AmountDueOnNextIntervalProps) => {
  const { isRebrand } = useTheme();
  const selectedCurrency = useContext(CurrencyContext);
  const currencyIsUSD = selectedCurrency.name === "USD";

  const amount = (
    <strong>
      <Price currency={selectedCurrency}>{planBillingAmount}</Price>
    </strong>
  );

  return (
    <div css={[nextIntervalCss, isRebrand && rebrandNextIntervalCss]}>
      <p>
        You authorize Zapier to charge you for the amount above.{" "}
        {!currencyIsUSD && "Price shown is valid for 24 hours."}
      </p>
      <p>
        Your subscription renews each {planInterval} until you cancel. The next
        charge will be {amount}, minus any applicable discounts,
        {nextPaymentDate && (
          <Fragment>
            {" "}
            on{" "}
            <strong>
              <DateTime date={nextPaymentDate} />
            </strong>
            {currencyIsUSD ? "." : ","}{" "}
          </Fragment>
        )}
        {!currencyIsUSD && (
          <Fragment>
            unless the currency exchange rate changes. The actual price is
            calculated using the exchange rate at the time of payment. Price
            charged excludes all taxes, levies and duties.
          </Fragment>
        )}
      </p>
      {extraTerm && <p>{extraTerm}</p>}
      {isPlanMetered && (
        <Fragment>
          Your plan includes{" "}
          <strong>{planNumFreeMeteredTasks.toLocaleString()} tasks</strong> per
          month. Extra tasks used will be charged monthly at the below rates.
        </Fragment>
      )}
      {isPlanMetered && (
        <div css={tableStyle}>
          <MeteredTiersTable tiers={meteredTiers} />
        </div>
      )}
      <p css={lastParagraphCss}>
        By clicking the button below, you understand and agree to these terms
        and Zapier’s{" "}
        <Link href="/privacy" target="_blank">
          privacy policy
        </Link>{" "}
        and{" "}
        <Link href="/terms" target="_blank">
          terms of service
        </Link>
        .
      </p>
    </div>
  );
};
